import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../api';

// Public page — reached from the "set your password" email link, before any
// login session exists. Mirrors Login.jsx's layout so it doesn't look like a
// different, unfinished app to someone opening it for the first time.
export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [target, setTarget] = useState(null);
  const [linkError, setLinkError] = useState('');

  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setLinkError('This link is missing its token.'); setChecking(false); return; }
    authApi.validateSetupToken(token)
      .then((res) => setTarget(res.data.data))
      .catch((err) => setLinkError(err.response?.data?.message || 'This link is invalid or has expired.'))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.newPassword.length < 6) return setError('Password must be at least 6 characters');
    if (form.newPassword !== form.confirmPassword) return setError('Passwords do not match');

    setLoading(true);
    try {
      await authApi.setPassword({ token, newPassword: form.newPassword });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-[55%] flex-col items-center justify-center p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%)' }}
      >
        <div className="absolute top-[-80px] right-[-80px] w-80 h-80 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #f59e0b, transparent)' }} />
        <div className="absolute bottom-[-60px] left-[-60px] w-64 h-64 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #6366f1, transparent)' }} />
        <div className="relative z-10 text-center max-w-sm">
          <div className="mb-10 flex justify-center">
            <img src="/sptl-logo.png" alt="SPTL" className="h-24 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3 leading-tight">Welcome to the Platform</h1>
          <p className="text-indigo-200 text-sm leading-relaxed">Set your password to get started.</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/sptl-logo.png" alt="SPTL" className="h-14 object-contain" />
          </div>

          <div className="bg-white rounded-2xl shadow-card-lg border border-slate-100 p-8">
            {checking ? (
              <p className="text-center text-slate-400 text-sm py-8">Checking your link…</p>
            ) : linkError ? (
              <div className="text-center py-4">
                <div className="mb-4 flex justify-center">
                  <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-red-500">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                    </svg>
                  </div>
                </div>
                <h2 className="text-lg font-bold text-slate-900 mb-2">Link invalid or expired</h2>
                <p className="text-slate-500 text-sm mb-6">{linkError}</p>
                <p className="text-slate-400 text-xs">Ask your admin to resend the "set password" email, or go to <a href="/login" className="text-indigo-600 underline">Sign In</a> if you already have a password.</p>
              </div>
            ) : done ? (
              <div className="text-center py-4">
                <div className="mb-4 flex justify-center">
                  <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-green-600">
                      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                </div>
                <h2 className="text-lg font-bold text-slate-900 mb-2">Password set</h2>
                <p className="text-slate-500 text-sm">Redirecting you to Sign In…</p>
              </div>
            ) : (
              <>
                <div className="mb-7">
                  <h2 className="text-2xl font-bold text-slate-900">Set your password</h2>
                  <p className="text-slate-400 text-sm mt-1">
                    Hi {target?.fullName || target?.username} — choose a password for your account (<span className="font-medium text-slate-500">{target?.username}</span>).
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="label">New Password</label>
                    <input
                      className="input"
                      type="password"
                      placeholder="At least 6 characters"
                      value={form.newPassword}
                      onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                      required
                      minLength={6}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="label">Confirm Password</label>
                    <input
                      className="input"
                      type="password"
                      placeholder="Re-enter your password"
                      value={form.confirmPassword}
                      onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-base mt-2">
                    {loading ? 'Setting password…' : 'Set Password'}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">Sobha Projects & Trade Pvt. Ltd.</p>
        </div>
      </div>
    </div>
  );
}
